import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@pfida-finance/ui/components';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EligibilityInfoModal({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#4b3e6d]">
            How your Estimated Eligibility date works
          </DialogTitle>
          <DialogClose />
        </DialogHeader>
        <hr className="border-none h-px bg-gray-200 -mx-6" />

        <div className="flex flex-col gap-5 pb-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#4b3e6d] mb-2">
              Our Community Mission
            </p>
            <div className="bg-[#e8f6ef] rounded-lg p-4 text-sm text-[#1b4230] leading-relaxed">
              While earning your points, you are helping other families in our community get into
              their forever homes. When you have earned enough points, you will be eligible to apply
              for finance and it will be your turn to receive community help.
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#4b3e6d] mb-2">
              How your Community Points are calculated
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">
              Every day you hold your GYS Home, PFIDAC, or Primary Finance Consortium investment,
              you earn Community Points. The more you invest, the faster you earn:
            </p>
            <div className="bg-[rgba(75,62,109,0.12)] rounded-lg px-4 py-2 text-sm font-semibold text-[#4b3e6d] font-mono my-2">
              Daily Points = Your investment ÷ 30,000
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              For example, a <strong>£30,000 investment</strong> earns 1 point per day (~30
              points/month). A <strong>£60,000 investment</strong> earns 2 points per day (~60
              points/month). Points accumulate automatically.
            </p>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#4b3e6d] mb-2">
              How your estimated eligibility date is determined
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">
              Once you have enough Community Points and your down payment is ready, you become
              eligible for home finance. Each month we work through the top 30 customers and reach
              out about next steps. Your estimated eligibility date reflects how long it will take
              you to meet both criteria at your current pace.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              The required number of points is based on your finance amount - larger finance amounts
              require more points.
            </p>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#4b3e6d] mb-2">
              How to change your date
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#e8f6ef] rounded-lg p-3">
                <p className="text-xs font-bold uppercase tracking-wide text-[#1b4230] mb-2">
                  Earlier ↑
                </p>
                <ul className="list-disc pl-4 text-xs text-[#1b4230] space-y-1 leading-relaxed">
                  <li>Increasing your GYS Home, PFIDAC, or Primary Finance Consortium investment</li>
                  <li>Investing your down payment as early as possible</li>
                  <li>Customers ahead of you leaving the queue</li>
                  <li>Invest during a window to accrue bonus credits</li>
                </ul>
              </div>
              <div className="bg-[#fff8e6] rounded-lg p-3">
                <p className="text-xs font-bold uppercase tracking-wide text-[#7a4a00] mb-2">
                  Later ↓
                </p>
                <ul className="list-disc pl-4 text-xs text-[#7a4a00] space-y-1 leading-relaxed">
                  <li>Down payment not yet ready</li>
                  <li>Other customers investing more into the community fund</li>
                  <li>Divesting your funds*</li>
                  <li>Increasing your finance requirements</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              * A minimum of £5,000 must be invested to maintain a spot on the waiting list.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
